import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { Box, Paper } from '@mui/material';
import { isSuccess } from 'src/utils/request';
import { ErrorLayout } from 'app/ErrorLayout';
import { PayError } from 'components/pay/errors/PayError';
import { PayHeader } from 'components/pay/header/PayHeader';
import { PayToolbar } from 'components/pay/header/PayToolbar';
import { useGiftCardModal } from 'components/pay/hooks/useGiftCardModal';
import { usePayActions } from 'components/pay/hooks/usePayActions';
import { usePayAtTable } from 'components/pay/hooks/usePayAtTable';
import { usePayQueryParams } from 'components/pay/hooks/usePayDateCheckId';
import { usePaymentMethods } from 'components/pay/hooks/usePaymentMethods';
import { usePayMyOrder } from 'components/pay/hooks/usePayMyOrder';
import { useOrderTips } from 'components/order/hooks/useOrderTips';
import { PayActionModal } from 'components/pay/modals/PayActionsModal';
import { PayBillSplittingModal } from 'components/pay/modals/PayBillSplittingModal';
import { PayGiftCardModal } from 'components/pay/modals/PayGiftCardModal';
import { PayTableSelector } from 'components/pay/modals/PayTableSelector';
import {
    getPaySession,
    getPaySessionSplits,
    setPaySession,
    setPaySessionSplits
} from 'components/pay/model/helpers';
import { PayItems } from 'components/pay/order-item/PayItems';
import { PayItemsHeader } from 'components/pay/order-item/PayItemsHeader';
import { PaymentButtons } from 'components/pay/payment-method/PaymentButtons';
import { PaymentMethods } from 'components/pay/payment-method/PaymentMethods';
import { PaymentBreakdown } from 'components/pay/PaymentBreakdown';
import { PayAwards } from 'components/pay/rewards/PayAwards';
import { PayTips } from 'components/pay/tips/PayTips';
import { PayLinkButton } from 'components/pay/ui/PayLinkButton';
import { ConfirmDialog } from 'lib/ConfirmDialog';
import { isDefined, isEmptyString, isNumber } from 'lib/typeInference';
import { useLocalHistory } from 'lib/useLocalHistory';
import { LocationRouteParams, ROUTES } from 'pages/routes';
import { setBasket, setDeliveryLocation } from 'store/basket/basketActions';
import { ApplicationState } from 'store/store';
import { useBillSplitting } from 'components/pay/hooks/useBillSplitting';
import { clearSessionStorage, roundToDecimal } from 'lib/helpers';
import {
    ExistingSharedOrderData,
    IAdjustmentLocalResource,
    OrderErrorCodes,
    OrderScenario
} from 'components/order/model/Order';
import { useGiftCards } from 'components/order/hooks/useGiftCards';
import { useOrderGiftcardCalculator } from 'components/order/hooks/usePayGiftcardCalculator';
import { useTransactionLimitValidation } from 'components/basket/hooks/useTransactionLimitValidation';
import { useQuery } from 'src/hooks/useQuery';
import { setOnCloseRedirectUrl } from 'components/user/localAuth';
import { PayCoupons } from 'components/pay/PayCoupons';
import { PayCardCheckError } from 'components/pay/errors/PayCardCheckError';
import { useOrderAwards } from 'components/order/hooks/useOrderAwards';
import { PayItemSplittingModal } from 'components/pay/modals/PayItemSplittingModal';
import { EGAEventName, useGAHelpers } from 'lib/useGAHelpers';
import { PayItemsAddedModal } from 'components/pay/modals/PayItemsAddedModal';

export const PAY_MAX_WIDTH = '550px';

export const PayPage = () => {
    const { merchantId, locationId } = useParams<LocationRouteParams>();
    const { checkId, tableNumber } = usePayQueryParams();
    const orderId = useQuery('orderId');
    const { replace, push } = useLocalHistory();
    const location = useLocation();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { logUserEvent } = useGAHelpers();
    const [openTableSelector, setOpenTableSelector] = React.useState(
        !isDefined(tableNumber) && !isDefined(checkId) && (!isDefined(orderId) || isEmptyString(orderId))
    );
    const [openBillSplitting, setOpenBillSplitting] = React.useState(false);
    const [existingParams, setExistingParams] = React.useState<ExistingSharedOrderData>(
        () => getPaySession(merchantId, locationId, orderId) ?? {}
    );
    const { isLoading: settingsLoading, settings } = useSelector((state: ApplicationState) => state.settings);
    const { isLoading: layoutLoading } = useSelector((state: ApplicationState) => state.layout);
    const { currentLocation, isCurrentLocationLoading } = useSelector(
        (state: ApplicationState) => state.locations
    );
    const { selectedPaymentMethod, onAddNewCardClick, ...paymentMethodsProps } = usePaymentMethods();

    const [showItemsAddedSuccessDialog, setShowItemsAddedSuccessDialog] = React.useState(
        !!location?.state?.showAddItemSuccess
    );
    const handleSuccessDialogClose = React.useCallback(() => {
        replace(ROUTES.QUICKPAY.CHECKOUT, {}, location.search);
        setShowItemsAddedSuccessDialog(false);
    }, [replace, location.search]);

    React.useEffect(() => {
        setExistingParams(getPaySession(merchantId, locationId, orderId) ?? {});
    }, [locationId, merchantId, orderId]);

    const {
        coupons,
        awards,
        isEmpty,
        orderDetails,
        orderTotals,
        isLoading: isOrderLoading,
        claimedItemIds,
        isOrderNotFound,
        operationError,
        multiPartOrderingEnabled,
        onAddMoreItems,
        onItemSplitSubmit
    } = usePayAtTable(existingParams);

    const { splitShares, yourShares, handleSplit, customSplit } = useBillSplitting(
        orderDetails?.splitTotal?.splitsToPay
    );

    const balance = React.useMemo(() => {
        if (!orderTotals || !orderDetails?.total || !orderTotals?.total) {
            return 0;
        }
        const orderDetailsTotal = orderTotals.total;
        const shouldUseBalanceToPay = orderDetailsTotal > orderDetails?.total.balance;
        const orderPrice = shouldUseBalanceToPay ? orderDetails?.total.balance : orderTotals.total;
        return orderPrice ?? 0;
    }, [orderDetails?.total, orderTotals]);

    const totalAfterSplit = React.useMemo(() => {
        if (!balance) {
            return 0;
        }
        if (customSplit) {
            if (customSplit > balance || Math.abs((balance ?? 0) - customSplit) <= 0.1) {
                return balance;
            }
            return customSplit;
        }
        if (yourShares && splitShares) {
            let dividedTotal = (balance / splitShares) * yourShares;
            if (roundToDecimal(dividedTotal, 3) - roundToDecimal(dividedTotal, 2) > 0) {
                dividedTotal = roundToDecimal(dividedTotal, 2) + 0.01;
            }
            if (Math.abs((balance ?? 0) - dividedTotal) <= 0.1) {
                dividedTotal = balance ?? 0;
            }
            return roundToDecimal(dividedTotal, 2);
        }
        return balance;
    }, [balance, customSplit, splitShares, yourShares]);

    const { availableAwards, selectedAdjustments, handleSetReward, handleClearRewards, awardsEnabled } =
        useOrderAwards(
            orderDetails?.availableAdjustments,
            orderDetails?.adjustments,
            totalAfterSplit,
            existingParams,
            awards.value,
            awards.platformAwardsEnabled
        );

    const handleAwardAdd = React.useCallback(
        (awardId: string) => {
            if (awards.platformAwardsEnabled) {
                const awardToAdd = availableAwards.find(item => item.awardId === awardId);
                awards.onAwardAdd({
                    type: awardToAdd?.type,
                    awardId: awardToAdd?.awardId,
                    quantity: awardToAdd?.quantity
                });
            } else {
                handleSetReward(awardId);
            }
        },
        [availableAwards, awards, handleSetReward]
    );

    const totalAfterReward = React.useMemo(() => {
        const selectedReward: undefined | IAdjustmentLocalResource = selectedAdjustments?.at(-1);
        if (awards.platformAwardsEnabled || !selectedReward) {
            return totalAfterSplit;
        }
        const afterRewardIsApplied = totalAfterSplit + (selectedReward.totalValue ?? 0);
        if (afterRewardIsApplied < 0) {
            return 0;
        }
        return afterRewardIsApplied;
    }, [awards.platformAwardsEnabled, selectedAdjustments, totalAfterSplit]);

    const {
        giftCard,
        giftCardBalance,
        giftCardError,
        giftCardLoading,
        gifcardPartiallyRedeemable,
        submitGiftCard,
        resetGiftCard,
        resetGiftCardErrors
    } = useGiftCards(existingParams?.giftCard);
    const { tipValue, onTipChange, tippingSettings, selectedTip } = useOrderTips(
        OrderScenario.TABLE,
        totalAfterReward,
        existingParams
    );
    const { isGiftCardsEnabled, showGiftCardModal, handleCloseGiftCard, handleOpenGiftCard } =
        useGiftCardModal();
    const { giftCardPayment, totalValue, giftCardRemainingBalance, giftCardOverpayment } =
        useOrderGiftcardCalculator(
            totalAfterReward,
            gifcardPartiallyRedeemable,
            tipValue,
            giftCard,
            giftCardBalance
        );

    const {
        payWithCard,
        isConfirmationOpen,
        onRetry,
        onErrorDialogClose,
        handleDigitalPayment,
        isSubmitting
    } = usePayMyOrder(
        selectedPaymentMethod,
        orderDetails,
        totalValue ?? 0,
        tipValue ?? 0,
        selectedTip,
        onAddNewCardClick,
        giftCardPayment,
        selectedAdjustments,
        coupons.value,
        yourShares,
        splitShares,
        claimedItemIds
    );
    const {
        showDialog: actionsMenuOpen,
        onClose: onActionsMenuClose,
        onOpen: onActionMenuOpen,
        isMenuAvailable
    } = usePayActions(openTableSelector, isOrderLoading, isOrderNotFound);

    React.useEffect(() => {
        if (isDefined(orderId) && merchantId) {
            const splitParams = getPaySessionSplits(merchantId, locationId, orderId);
            clearSessionStorage(merchantId, true);
            const params: ExistingSharedOrderData = {
                selectedRewards: selectedAdjustments,
                tipValue,
                giftCard,
                userSelectedTipValue: selectedTip,
                coupons: coupons.value,
                claimedItemIds
            };
            setExistingParams(params);
            setPaySession(params, merchantId, locationId, orderId);
            setPaySessionSplits(splitParams, merchantId, locationId, orderId);
        }
    }, [
        selectedAdjustments,
        selectedTip,
        tipValue,
        giftCard,
        merchantId,
        locationId,
        coupons.value,
        orderId,
        claimedItemIds
    ]);

    React.useEffect(() => {
        setBasket({ locationId: currentLocation?._id })(dispatch);
    }, [currentLocation?._id, dispatch]);

    React.useEffect(() => {
        if (
            !isDefined(tableNumber) &&
            !isDefined(checkId) &&
            (!isDefined(orderId) || isEmptyString(orderId))
        ) {
            setOpenTableSelector(true);
        }
    }, [checkId, orderId, tableNumber]);

    React.useEffect(() => {
        if (isDefined(tableNumber) && !isEmptyString(tableNumber)) {
            setDeliveryLocation(tableNumber)(dispatch);
        }
    }, [tableNumber, dispatch]);

    const handleTableChange = React.useCallback(
        (tNumber?: string, cId?: string) => {
            handleClearRewards();
            handleSplit();
            if (!isEmptyString(cId)) {
                replace('', {}, `checkId=${cId}`);
            } else {
                replace('', {}, `tableNumber=${tNumber}`);
            }
            setOpenTableSelector(false);
        },
        [handleClearRewards, handleSplit, replace]
    );
    const handleChangeTableClick = React.useCallback(() => {
        setOpenTableSelector(true);
    }, []);
    const handleChangeTableClose = React.useCallback(() => {
        setOpenTableSelector(false);
    }, []);
    const handleBillSplittingClose = React.useCallback(() => {
        setOpenBillSplitting(false);
    }, []);
    const [openItemSplitting, setOpenItemSplitting] = React.useState(false);
    const handleItemSplittingOpen = React.useCallback(() => {
        logUserEvent(EGAEventName.PatSplitByItemClick);
        handleBillSplittingClose();
        setOpenItemSplitting(true);
    }, [handleBillSplittingClose, logUserEvent]);
    const handleItemSplittingClose = React.useCallback(() => {
        setOpenItemSplitting(false);
    }, []);
    const handleSubmitSplit = React.useCallback(
        (ids: number[]) => {
            onItemSplitSubmit(ids);
            setOpenItemSplitting(false);
        },
        [onItemSplitSubmit]
    );
    const handleBillSplittingOpen = React.useCallback(() => {
        if (claimedItemIds.length > 0) {
            handleItemSplittingOpen();
            logUserEvent(EGAEventName.PatItemSplitChangeSplitClick);
        } else {
            setOpenBillSplitting(true);
        }
    }, [claimedItemIds.length, handleItemSplittingOpen, logUserEvent]);

    const handleAwardsClear = React.useCallback(() => {
        logUserEvent(EGAEventName.AwardsClearClick);
        if (awards.platformAwardsEnabled) {
            awards.onClear();
        } else {
            handleClearRewards();
        }
    }, [awards, handleClearRewards, logUserEvent]);

    const globalLoading = React.useMemo(
        () => settingsLoading || layoutLoading,
        [layoutLoading, settingsLoading]
    );

    const operationErrorData = React.useMemo(() => {
        if (isSuccess(operationError) && operationError.data) {
            return operationError.data;
        }
        return null;
    }, [operationError]);

    const {
        isOutOfLimit,
        handleExceededLimitDialogClose,
        handleValidateTransactionLimit,
        showExceededLimitDialog,
        transactionLimitDescription
    } = useTransactionLimitValidation(totalValue);

    const handlePayWithCard = React.useCallback(
        (forced?: boolean) => {
            if (isOutOfLimit) {
                handleValidateTransactionLimit();
            } else {
                payWithCard(forced);
            }
        },
        [handleValidateTransactionLimit, isOutOfLimit, payWithCard]
    );

    const handleSignIn = React.useCallback(() => {
        if (merchantId) {
            setOnCloseRedirectUrl(location.pathname + location.search, merchantId);
            push(ROUTES.USER.LOGIN);
        }
    }, [location.pathname, location.search, merchantId, push]);
    const handleAddPaymentMethod = React.useCallback(() => {
        if (merchantId) {
            setOnCloseRedirectUrl(location.pathname + location.search, merchantId);
            push(ROUTES.USER.PAYMENT_METHODS);
        }
    }, [location.pathname, location.search, merchantId, push]);

    const isCardCheckError = React.useMemo(
        () => operationErrorData && operationErrorData?.errorCode === OrderErrorCodes.CARD_CHECK_ERROR,
        [operationErrorData]
    );

    const canChangeTableNumber = React.useMemo(() => {
        const isTableSelected = isDefined(tableNumber) || isDefined(orderDetails?.deliveryLocation);
        if (isTableSelected) {
            return settings?.payAtTable?.canChangeTableNumber;
        }
        return true;
    }, [settings?.payAtTable?.canChangeTableNumber, tableNumber, orderDetails?.deliveryLocation]);

    const showActionModal = React.useMemo(
        () => !isCardCheckError && !operationErrorData && (actionsMenuOpen || isEmpty),
        [actionsMenuOpen, isCardCheckError, isEmpty, operationErrorData]
    );

    if (!currentLocation && !isCurrentLocationLoading) {
        return <ErrorLayout title={t('GENERAL_GENERIC_ERROR')} />;
    }
    return (
        <Box
            height="100%"
            width="100%"
            className="hidden-scroll"
            marginX="auto"
            maxWidth={PAY_MAX_WIDTH}
            overflow="scroll"
        >
            <Paper style={{ minHeight: '100%' }}>
                <PayHeader variant={isDefined(yourShares) || isNumber(customSplit) ? 'large' : 'default'}>
                    <PayToolbar
                        loading={globalLoading}
                        table={tableNumber ?? orderDetails?.deliveryLocation}
                        checkId={checkId ?? orderDetails?.checkId}
                        disableBack
                        onChangeClick={handleChangeTableClick}
                        onMenuClick={onActionMenuOpen}
                        showMenu={isMenuAvailable}
                        disableEdit={!canChangeTableNumber}
                    />
                    {!isOrderNotFound && !operationErrorData && !isEmpty && (
                        <PayItemsHeader
                            loading={isOrderLoading}
                            total={balance + (tipValue ?? 0)}
                            splitTotal={!!yourShares ? totalAfterReward : undefined}
                            customSplit={isNumber(customSplit) ? customSplit + (tipValue ?? 0) : undefined}
                        />
                    )}
                </PayHeader>
                {!isOrderNotFound && !operationErrorData && !isEmpty && (
                    <React.Fragment>
                        <PayItems
                            loading={isOrderLoading}
                            items={orderDetails?.items}
                            claimedItems={claimedItemIds}
                        />
                        <PayAwards
                            disabled={!awardsEnabled}
                            loading={isOrderLoading}
                            awards={availableAwards}
                            selected={selectedAdjustments}
                            onSelect={handleAwardAdd}
                            onUnselect={awards.onAwardRemove}
                            onClear={handleAwardsClear}
                            showAddMoreButton={awards.showMoreAwardsButton}
                            onAddMore={awards.onMultipleAwardsEnable}
                            multi={awards.platformAwardsEnabled}
                        />
                        {coupons.showCoupons && (
                            <PayCoupons
                                coupons={coupons.couponAdjustments}
                                onAdd={coupons.onCouponAdd}
                                loading={isOrderLoading}
                                onRemove={coupons.onCouponRemove}
                            />
                        )}
                        <PayTips
                            loading={isOrderLoading}
                            selectedTip={selectedTip}
                            onChange={onTipChange}
                            tipValue={tipValue}
                            tippingSettings={tippingSettings}
                            subTotal={totalAfterReward}
                        />
                        <PaymentMethods
                            selectedPaymentMethod={selectedPaymentMethod}
                            onAddNewCardClick={onAddNewCardClick}
                            {...paymentMethodsProps}
                            loading={isOrderLoading}
                        />
                        {isGiftCardsEnabled && !isOrderLoading && (
                            <Box px={2}>
                                <PayLinkButton onClick={giftCard ? resetGiftCard : handleOpenGiftCard}>
                                    {giftCard
                                        ? t('PAT_QUICKPAY_REMOVE_GIFTCARD')
                                        : t('PAT_QUICKPAY_APPLY_GIFTCARD')}
                                </PayLinkButton>
                            </Box>
                        )}
                        <PaymentBreakdown
                            loading={isOrderLoading}
                            total={orderTotals}
                            tip={tipValue ?? 0}
                            giftCardPayment={giftCardPayment}
                            payments={orderDetails?.payments}
                            rewards={selectedAdjustments}
                            multi={awards.platformAwardsEnabled}
                        />
                        {!isOrderNotFound && (
                            <PaymentButtons
                                loading={isOrderLoading}
                                amount={totalValue}
                                onClick={handlePayWithCard}
                                onDigitalPaymentMade={handleDigitalPayment}
                                selectedWallet={selectedPaymentMethod?.type}
                                cards={paymentMethodsProps.cards}
                                onBillSplit={handleBillSplittingOpen}
                                isSplit={!!yourShares || isDefined(customSplit)}
                                isItemSplit={claimedItemIds.length > 0}
                                outOfTransactionLimit={isOutOfLimit}
                                multiPartOrderingEnabled={multiPartOrderingEnabled}
                                onAddMoreItems={onAddMoreItems}
                                disabled={isSubmitting}
                            />
                        )}
                    </React.Fragment>
                )}
                {!!operationErrorData && isCardCheckError && (
                    <PayCardCheckError
                        errorMessage={operationErrorData?.errorMessage}
                        onSignIn={handleSignIn}
                        onAddPaymentMethod={handleAddPaymentMethod}
                    />
                )}
                {!!operationErrorData && !isCardCheckError && (
                    <PayError
                        errorMessage={operationErrorData?.errorMessage}
                        errorCode={operationErrorData?.errorCode}
                    />
                )}
                <PayTableSelector
                    open={openTableSelector && !globalLoading}
                    disableBackdropClick={
                        !isDefined(tableNumber) &&
                        !isDefined(checkId) &&
                        !isDefined(orderId) &&
                        !isEmptyString(orderId)
                    }
                    onChange={handleTableChange}
                    onClose={handleChangeTableClose}
                />
                <ConfirmDialog
                    open={isConfirmationOpen}
                    disableBackdropClick
                    disableEscapeKeyDown
                    onClose={onErrorDialogClose}
                    confirmMessage={t('RETRY')}
                    onConfirm={onRetry}
                    description={t('DIALOG_ORDER_STATUS_UNKNOWN')}
                />
                <ConfirmDialog
                    open={showExceededLimitDialog}
                    disableBackdropClick
                    disableEscapeKeyDown
                    isCancellable={false}
                    onClose={handleExceededLimitDialogClose}
                    confirmMessage={t('OK')}
                    onConfirm={handleExceededLimitDialogClose}
                    description={transactionLimitDescription}
                    title={t('DIALOG_OOPS')}
                    align="center"
                />
                <PayActionModal
                    open={showActionModal && !showItemsAddedSuccessDialog}
                    onClose={onActionsMenuClose}
                    handleChange={handleChangeTableClick}
                    table={tableNumber ?? orderDetails?.deliveryLocation}
                    checkId={checkId ?? orderDetails?.checkId}
                    orderNotFound={isOrderNotFound || isEmpty}
                    onBillSplit={handleBillSplittingOpen}
                    onAddMoreItems={onAddMoreItems}
                />
                <PayBillSplittingModal
                    open={openBillSplitting}
                    onClose={handleBillSplittingClose}
                    total={balance}
                    limit={balance}
                    customAmount={customSplit}
                    onApply={handleSplit}
                    shares={splitShares}
                    yourShares={yourShares}
                    onItemSplittingOpen={handleItemSplittingOpen}
                />
                <PayItemSplittingModal
                    open={openItemSplitting}
                    onClose={handleItemSplittingClose}
                    orderDetails={orderDetails}
                    onSubmit={handleSubmitSplit}
                />
                <PayItemsAddedModal open={showItemsAddedSuccessDialog} onCancel={handleSuccessDialogClose} />
                {isGiftCardsEnabled && (
                    <PayGiftCardModal
                        open={showGiftCardModal}
                        onClose={handleCloseGiftCard}
                        loading={isOrderLoading || giftCardLoading}
                        onGiftcardApply={submitGiftCard}
                        error={giftCardError}
                        giftCardPayment={giftCardPayment?.amount}
                        remainingBalance={giftCardRemainingBalance}
                        resetErrors={resetGiftCardErrors}
                        overpayment={giftCardOverpayment}
                        orderBalance={totalAfterReward}
                        gitcardBalance={giftCardBalance}
                    />
                )}
            </Paper>
        </Box>
    );
};
